<template>
  <a-modal
    :visible="visible"
    centered
    @cancel="handleCancel"
    width="70%"
    :footer="false"
  >
    <template slot="title">
      <div class="text-center">
        <h4 class="mb-0">{{ $t("bulletin.duplicateSettings") }}</h4>
      </div>
    </template>

    <div class="p-3">
      <!-- Source Section -->
      <div class="mb-4">
        <a-divider orientation="left">{{ $t("bulletin.source") }}</a-divider>
        <div class="row g-3">
          <div class="col-md-2">
            <div class="d-flex justify-content-center align-items-center h-100">
              <a-switch
                class="w-100"
                size="default"
                default-checked
                v-model="onParams"
                :checkedChildren="$t('bulletin.parameters')"
                :unCheckedChildren="$t('bulletin.templates')"
              />
            </div>
          </div>

          <template v-if="onParams">
            <div class="col-md-3">
              <label class="form-label">{{ $t("etablissement.type") }}</label>
              <a-select
                class="w-100"
                :value="sourceType"
                @change="sourceType = $event"
              >
                <a-select-option
                  v-for="(value, key) in builletinTypesNames"
                  :key="key"
                  :value="key"
                  :disabled="!allowedParam.types.has(key)"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("etablissement.niveau") }}</label>
              <a-select
                class="w-100"
                :value="sourceLevel"
                @change="sourceLevel = $event"
              >
                <a-select-option
                  v-for="(value, name, index) in levels"
                  :key="index"
                  :value="name"
                  :disabled="!allowedParam.levels.has(Number(name))"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-2">
              <label class="form-label">{{ $t("emploi.trimestre") }}</label>
              <a-select
                class="w-100"
                :value="sourceTrim"
                @change="sourceTrim = $event"
                default-value="1"
              >
                <a-select-option
                  v-for="(value, index) in ['1', '2', '3']"
                  :key="index"
                  :value="value"
                  :disabled="!allowedParam.trims.has(Number(value))"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </div>

            <div class="col-md-3">
              <label class="form-label">{{ $t("bulletin.schoolYear") }}</label>
              <a-select
                class="w-100"
                :value="sourceSchoolarYear"
                @change="sourceSchoolarYear = $event"
                :placeholder="$t('bulletin.selectSchoolYear')"
              >
                <a-select-option
                  v-for="schoolYear in schoolYears"
                  :key="schoolYear._id"
                  :value="schoolYear._id"
                >
                  {{ schoolYear.label }}
                </a-select-option>
              </a-select>
            </div>
          </template>

          <div class="col-md-10" v-if="!onParams">
            <a-select
              class="w-100"
              :value="sourceTemplate"
              @change="selectTemplate"
              :placeholder="$t('bulletin.selectTemplate')"
            >
              <a-select-option
                v-for="(template, index) in templateList"
                :key="index"
                :value="template._id"
              >
                {{ template.templateName }}
              </a-select-option>
            </a-select>
          </div>
        </div>
      </div>

      <!-- Target Section -->
      <div class="mb-4">
        <a-divider orientation="left">{{ $t("bulletin.targets") }}</a-divider>
        <div class="row g-3">
          <div class="col-md-2">
            <a-button type="primary" class="w-100 mt-4" @click="selectAll">
              {{ $t("sms.selectAll") }}
            </a-button>
          </div>

          <div class="col-md-3">
            <label class="form-label">{{ $t("etablissement.type") }}</label>
            <a-select
              class="w-100"
              :value="targetTypes"
              mode="multiple"
              allow-clear
              @change="targetTypes = $event"
              :placeholder="$t('bulletin.selectTypes')"
            >
              <a-select-option
                v-for="(value, key) in builletinTypesNames"
                :key="key"
                :value="key"
                :disabled="
                  (sourceType == 'MP' && key != 'MP') ||
                  (sourceType != 'MP' && key == 'MP')
                "
              >
                {{ value }}
              </a-select-option>
            </a-select>
          </div>

          <div class="col-md-2">
            <label class="form-label">{{ $t("etablissement.niveau") }}</label>
            <a-select
              class="w-100"
              :value="targetLevels"
              mode="multiple"
              allow-clear
              @change="targetLevels = $event"
              :placeholder="$t('bulletin.selectLevels')"
            >
              <a-select-option
                v-for="(value, name, index) in levels"
                :key="index"
                :value="name"
              >
                {{ value }}
              </a-select-option>
            </a-select>
          </div>

          <div class="col-md-2">
            <label class="form-label">{{ $t("emploi.trimestre") }}</label>
            <a-select
              class="w-100"
              mode="multiple"
              allow-clear
              :value="targetTrims"
              @change="targetTrims = $event"
              :placeholder="$t('bulletin.selectTrimesters')"
            >
              <a-select-option
                v-for="(value, index) in ['1', '2', '3']"
                :key="index"
                :value="value"
              >
                {{ value }}
              </a-select-option>
            </a-select>
          </div>

          <div class="col-md-3">
            <label class="form-label">{{ $t("bulletin.schoolYear") }}</label>
            <a-select
              class="w-100"
              :value="targetSchoolarYear"
              @change="targetSchoolarYear = $event"
              :placeholder="$t('bulletin.selectSchoolYear')"
            >
              <a-select-option
                v-for="schoolYear in schoolYears"
                :key="schoolYear._id"
                :value="schoolYear._id"
              >
                {{ schoolYear.label }}
              </a-select-option>
            </a-select>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="row mt-4">
        <div class="col-md-4 offset-md-8">
          <a-button type="primary" class="w-100" @click="clone" :disabled="isReadOnly">
            <a-icon type="save" />
            {{ $t("all.apply") }}
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import { mapState, mapGetters } from "vuex";

export default {
  name: "SysGestLMSParamsClone",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    levels: {
      type: Object,
      required: true,
    },
    builletinTypesNames: {
      type: Object,
      required: true,
    },
  },
  watch: {
    visible: {
      handler: async function (newVal, _) {
        if (newVal) {
          await this.fetchSchoolYears();
          await apiClient
            .post("/bulletin/v2/affichage/list", {
              status: "active",
            })
            .then(({ data }) => {
              this.configList = data;
            });
        }
      },
    },
  },
  computed: {
    ...mapState(["settings"]),
    ...mapGetters("settings", ["packDetailsGetter"]),
    isReadOnly() {
      const value = this.packDetailsGetter?.access?.edManager?.displaySettings;
      return value === "ro";
    },
    templateList() {
      return this.configList.filter((config) => config.isTemplate);
    },
    paramsList() {
      return this.configList.filter((config) => !config.isTemplate);
    },
    allowedParam() {
      const types = new Set();
      const levels = new Set();
      const trims = new Set();
      this.paramsList.forEach((param) => {
        types.add(param.type);
        levels.add(Number(param.level));
        trims.add(Number(param.trimester));
      });
      return {
        types,
        levels,
        trims,
      };
    },
    schoolYears() {
      return this.schoolYearsList.map((sy) => ({
        _id: sy._id,
        label: this.formatSchoolYear(sy),
      }));
    },
  },
  data() {
    return {
      configList: [],
      onParams: true,
      sourceLevel: "",
      sourceType: "",
      sourceTemplate: "",
      sourceTrim: "",
      sourceSchoolarYear: "",
      currentStep: 1,
      targetLevels: [],
      targetTypes: [],
      targetTrims: [],
      targetSchoolarYear: "",
      schoolYearsList: [],
    };
  },
  methods: {
    handleApiError(error, defaultMessage) {
      // Check for PACK_PERMISSION_ERROR
      if (
        error.response?.data?.errorCode === "PACK_PERMISSION_ERROR" ||
        error.response?.data?.error?.errorCode === "PACK_PERMISSION_ERROR"
      ) {
        this.$message.error(this.$t("errorCode.PACK_PERMISSION_ERROR"));
        return;
      }
      // Fallback to default error message
      this.$message.error(
        error.response?.data?.message || defaultMessage || this.$t("error.erreur")
      );
    },
    formatSchoolYear(sy) {
      if (!sy || !sy.start || !sy.end) return "";
      const start = moment(sy.start).format("YYYY");
      const end = moment(sy.end).format("YYYY");
      return `${start}/${end}`;
    },
    async fetchSchoolYears() {
      try {
        const response = await apiClient.post("/schoolaryears/filter", {
          query: {},
        });
        this.schoolYearsList = response.data.map((elem) => ({
          ...elem,
          key: elem._id,
        }));
        if (this.schoolYearsList.length > 0) {
          this.sourceSchoolarYear =
            this.settings.activeSchoolarYear || this.schoolYearsList[0]._id;
          this.targetSchoolarYear =
            this.settings.activeSchoolarYear || this.schoolYearsList[0]._id;
        }
      } catch (error) {
        console.error("Error fetching school years:", error);
        this.$message.error(this.$t("error.errorLoadingSchoolYears"));
      }
    },
    handleCancel() {
      this.$emit("hide");
    },
    selectTemplate(selectedTempalte) {
      this.sourceTemplate = selectedTempalte;
      const template = this.templateList.find(
        (template) => template._id == selectedTempalte
      );
      if (template) this.sourceType = template.type;
      console.info(this.sourceType);
    },
    selectAll() {
      if (this.sourceType == "MP") {
        this.targetTypes = ["MP"];
      } else {
        this.targetTypes = ["MT", "MS"];
      }
      this.targetLevels = Object.keys(this.levels);
      this.targetTrims = ["1", "2", "3"];
    },
    clone() {
      apiClient
        .post("/bulletin/v2/affichage/clone", {
          onParams: this.onParams,
          sourceTemplate: this.sourceTemplate,
          sourceType: this.sourceType,
          sourceLevel: this.sourceLevel,
          sourceTrim: this.sourceTrim,
          sourceSchoolarYear: this.sourceSchoolarYear,
          targetLevels: this.targetLevels,
          targetTrims: this.targetTrims,
          targetTypes: this.targetTypes,
          targetSchoolarYear: this.targetSchoolarYear,
        })
        .then((_) => {
          this.$message.success(this.$t("bulletin.allDataDuplicatedSuccess"));
          this.$message.info(this.$t("bulletin.pleaseRefreshPage"));
          this.resetForm();
          this.$emit("cloneHappened");
        })
        .catch((e) => {
          console.info(e);
          this.handleApiError(e, this.$t("error.erreur"));
        });
    },
    resetForm() {
      this.onParams = true;
      this.sourceLevel = "";
      this.sourceType = "";
      this.sourceTemplate = "";
      this.sourceTrim = "";
      this.sourceSchoolarYear = "";
      this.targetLevels = [];
      this.targetTypes = [];
      this.targetTrims = [];
      this.targetSchoolarYear = "";
    },
  },
};
</script>
